// src/components/common/Icons.js
// 78차: design_handoff_unlock 프로젝트(claude.ai/design)에 실제로 만들어져 있던 SVG 아이콘
// 산출물을 코드로 옮김 — 지금까지 이 아이콘들을 "이미지 생성이 필요한 디자인 자산 갭"으로
// 잘못 안내했었음(실제로는 이미 만들어져 있었고 react-native-svg로 그대로 그리면 되는
// 것들이었음). LogoMark(AppHeader.js)와 동일하게 react-native-svg 컴포넌트로 구현.
//
// 빈 화면 일러스트 7종(96x96, 이중 원 프레임 + 골드 라인 아이콘) — FavoritesScreen,
// NotificationsScreen, PointHistoryScreen, EscapeDashboardScreen 기록, 플랜 잠금 카드 등에서 사용.
// 마이페이지 메뉴 아이콘 12종(24x24, 골드 스트로크) — MyPageScreen 메뉴 리스트에서 사용.
import React from 'react';
import { View, Text, StyleSheet } from 'react-native';
import Svg, { Circle, Rect, Path, G } from 'react-native-svg';
import { COLORS } from '../../constants/theme';

const GOLD = COLORS.gold;

// 78차: "🏪 {매장명}" 처럼 이모지+텍스트 한 줄로 된 곳을 아이콘+텍스트로 바꿀 때
// 매번 View+Icon+Text를 반복 작성하지 않도록 하는 공용 헬퍼. numberOfLines 등은
// 안쪽 Text에 그대로 전달.
// 78차 15차: GradientButton의 padding 분리(78차)와 동일한 원인의 버그 — 호출부가
// 이모지+Text 한 줄이던 시절의 스타일(예: 섹션 제목의 marginBottom)을 그대로
// textStyle에 넘기는 경우가 많은데, margin이 안쪽 Text에 얹히면 이 row가
// alignItems:'center'라서 margin만큼 Text의 마진 박스가 아이콘보다 커져
// 텍스트 글자가 아이콘 중심보다 위로 떠 보이는 줄맞춤 버그가 생김(SuperAdminScreen.js의
// "계약 사업자 상세"/"계약 지점 상세"/"회원 목록" 등에서 스크린샷으로 재신고됨).
// textStyle의 margin 계열 값만 골라 바깥 wrapper로 옮겨 적용 — 아이콘과 텍스트는
// 항상 순수 텍스트 스타일(색/굵기 등)만으로 같은 행에서 중앙 정렬되고, 다음 요소와의
// 간격은 바깥 wrapper의 마진이 담당하게 됨.
export function IconLabel({ Icon, children, size = 13, color = GOLD, textStyle, style, numberOfLines, gap = 4 }) {
  const flatText = StyleSheet.flatten(textStyle) || {};
  const { margin, marginTop, marginBottom, marginLeft, marginRight, marginVertical, marginHorizontal, ...restTextStyle } = flatText;
  const outerMargin = {};
  if (margin !== undefined) outerMargin.margin = margin;
  if (marginTop !== undefined) outerMargin.marginTop = marginTop;
  if (marginBottom !== undefined) outerMargin.marginBottom = marginBottom;
  if (marginLeft !== undefined) outerMargin.marginLeft = marginLeft;
  if (marginRight !== undefined) outerMargin.marginRight = marginRight;
  if (marginVertical !== undefined) outerMargin.marginVertical = marginVertical;
  if (marginHorizontal !== undefined) outerMargin.marginHorizontal = marginHorizontal;
  return (
    <View style={[{ flexDirection: 'row', alignItems: 'center', gap }, style, outerMargin]}>
      <Icon size={size} color={color} />
      {/* numberOfLines로 말줄임하는 곳이 많아, row 안에서 Text가 아이콘을 밀어내지
          않고 남은 폭 안에서만 줄어들도록 flexShrink 기본 적용 */}
      <Text style={[{ flexShrink: 1 }, restTextStyle]} numberOfLines={numberOfLines}>{children}</Text>
    </View>
  );
}

// ── 빈 화면 일러스트 (96x96 원본, size로 스케일) ──────────────
function EmptyFrame({ size, color = GOLD, children }) {
  return (
    <Svg width={size} height={size} viewBox="0 0 96 96" fill="none">
      <Circle cx="48" cy="48" r="40" stroke={color} strokeWidth="1.4" strokeOpacity="0.28" />
      <Circle cx="48" cy="48" r="31" stroke={color} strokeWidth="1.4" strokeOpacity="0.16" />
      {children}
    </Svg>
  );
}

export function EmptyFavoritesIcon({ size = 64 }) {
  return (
    <EmptyFrame size={size}>
      <Path d="M48 64s-16-9.5-16-21a9 9 0 0 1 16-5.6A9 9 0 0 1 64 43c0 11.5-16 21-16 21z" stroke={GOLD} strokeWidth="2" strokeLinejoin="round" />
      <Path d="M40 30l4 4M56 30l-4 4" stroke={GOLD} strokeWidth="1.6" strokeLinecap="round" strokeOpacity="0.5" />
    </EmptyFrame>
  );
}

export function EmptyNotificationsIcon({ size = 64 }) {
  return (
    <EmptyFrame size={size}>
      <Rect x="30" y="36" width="36" height="26" rx="4" stroke={GOLD} strokeWidth="2" strokeLinejoin="round" />
      <Path d="M30 40l18 13 18-13" stroke={GOLD} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      <Circle cx="66" cy="34" r="7" fill={COLORS.bg} stroke={GOLD} strokeWidth="2" />
      <Path d="M63 34h6" stroke={GOLD} strokeWidth="2" strokeLinecap="round" />
    </EmptyFrame>
  );
}

export function EmptyPointsIcon({ size = 64 }) {
  return (
    <EmptyFrame size={size}>
      <Path d="M36 36h24l8 10-20 22-20-22 8-10z" stroke={GOLD} strokeWidth="2" strokeLinejoin="round" />
      <Path d="M28 46h40M42 36l-6 10 12 22M54 36l6 10-12 22" stroke={GOLD} strokeWidth="1.6" strokeLinejoin="round" strokeOpacity="0.6" />
    </EmptyFrame>
  );
}

export function EmptyStoresIcon({ size = 64 }) {
  return (
    <EmptyFrame size={size}>
      <Path d="M32 44l4-10h24l4 10" stroke={GOLD} strokeWidth="2" strokeLinejoin="round" />
      <Path d="M32 44v20h32V44" stroke={GOLD} strokeWidth="2" strokeLinejoin="round" />
      <Path d="M32 44a5 5 0 0 0 10 0 5 5 0 0 0 12 0 5 5 0 0 0 10 0" stroke={GOLD} strokeWidth="2" strokeLinejoin="round" />
      <Path d="M43 64v-9h10v9" stroke={GOLD} strokeWidth="1.8" strokeLinejoin="round" strokeOpacity="0.6" />
    </EmptyFrame>
  );
}

export function EscapeSuccessIcon({ size = 64 }) {
  return (
    <EmptyFrame size={size}>
      <Path d="M36 30h24v8a12 12 0 0 1-24 0v-8z" stroke={GOLD} strokeWidth="2" strokeLinejoin="round" />
      <Path d="M36 34h-6a6 6 0 0 0 6 6M60 34h6a6 6 0 0 1-6 6" stroke={GOLD} strokeWidth="2" strokeLinejoin="round" />
      <Path d="M48 50v8M42 66h12M44 66v-4h8v4" stroke={GOLD} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </EmptyFrame>
  );
}

export function EscapeFailIcon({ size = 64 }) {
  return (
    <EmptyFrame size={size}>
      <Circle cx="48" cy="48" r="17" stroke={GOLD} strokeWidth="2" />
      <Path d="M48 40v9l6 4" stroke={GOLD} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      <Path d="M38 28l4 4M58 28l-4 4" stroke={GOLD} strokeWidth="1.8" strokeLinecap="round" strokeOpacity="0.55" />
    </EmptyFrame>
  );
}

export function PlanLockedIcon({ size = 64 }) {
  return (
    <EmptyFrame size={size}>
      <Rect x="34" y="46" width="28" height="22" rx="5" stroke={GOLD} strokeWidth="2" strokeLinejoin="round" />
      <Path d="M39 46v-6a9 9 0 0 1 18 0v6" stroke={GOLD} strokeWidth="2" strokeLinecap="round" />
      <Circle cx="48" cy="55" r="3" fill={GOLD} />
      <Path d="M48 58v5" stroke={GOLD} strokeWidth="2" strokeLinecap="round" />
    </EmptyFrame>
  );
}

// ── 마이페이지 메뉴 아이콘 (24x24 원본, size로 스케일) ─────────
export function MenuReservationIcon({ size = 18, color = GOLD }) {
  return (
    <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <Rect x="3" y="5" width="18" height="16" rx="3" stroke={color} strokeWidth="1.7" />
      <Path d="M3 9h18M8 3v4M16 3v4" stroke={color} strokeWidth="1.7" strokeLinecap="round" />
    </Svg>
  );
}

export function MenuEscapeIcon({ size = 18, color = GOLD }) {
  return (
    <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <Rect x="5" y="10" width="14" height="11" rx="2.5" stroke={color} strokeWidth="1.7" />
      <Path d="M8 10V7a4 4 0 0 1 8 0v3" stroke={color} strokeWidth="1.7" />
    </Svg>
  );
}

export function MenuReviewIcon({ size = 18, color = GOLD }) {
  return (
    <Svg width={size} height={size} viewBox="0 0 12 12">
      <Path d="M6 1l1.5 3 3.3.4-2.4 2.3.6 3.3L6 8.7 2.9 9.9l.6-3.3L1.1 4.4 4.5 4z" fill={color} />
    </Svg>
  );
}

export function MenuPointsIcon({ size = 18, color = GOLD }) {
  return (
    <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <Circle cx="12" cy="12" r="9" stroke={color} strokeWidth="1.7" />
      <Path d="M12 7v5l3 2" stroke={color} strokeWidth="1.7" strokeLinecap="round" />
    </Svg>
  );
}

export function MenuFavoriteIcon({ size = 18, color = GOLD }) {
  return (
    <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <Path d="M12 20s-7-4.5-7-10a4 4 0 0 1 7-2.5A4 4 0 0 1 19 10c0 5.5-7 10-7 10z" stroke={color} strokeWidth="1.7" strokeLinejoin="round" />
    </Svg>
  );
}

export function MenuBellIcon({ size = 18, color = GOLD }) {
  return (
    <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <Path d="M12 3a5 5 0 0 0-5 5c0 5-2 6-2 6h14s-2-1-2-6a5 5 0 0 0-5-5z" stroke={color} strokeWidth="1.7" strokeLinejoin="round" />
      <Path d="M10 20a2 2 0 0 0 4 0" stroke={color} strokeWidth="1.7" />
    </Svg>
  );
}

export function MenuRecruitIcon({ size = 18, color = GOLD }) {
  return (
    <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <Circle cx="9" cy="7" r="3" stroke={color} strokeWidth="1.7" />
      <Path d="M3 20c0-3 3-4.5 6-4.5M16 8h5M18.5 5.5v5" stroke={color} strokeWidth="1.7" strokeLinecap="round" />
    </Svg>
  );
}

export function MenuInquiryIcon({ size = 18, color = GOLD }) {
  return (
    <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <Path d="M4 5h16v11H8l-4 3z" stroke={color} strokeWidth="1.7" strokeLinejoin="round" />
    </Svg>
  );
}

export function MenuNoticeIcon({ size = 18, color = GOLD }) {
  return (
    <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <Path d="M4 5h13l3 3v11H4z" stroke={color} strokeWidth="1.7" strokeLinejoin="round" />
      <Path d="M8 10h8M8 14h5" stroke={color} strokeWidth="1.7" strokeLinecap="round" />
    </Svg>
  );
}

export function MenuNicknameIcon({ size = 18, color = GOLD }) {
  return (
    <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <Circle cx="12" cy="8" r="4" stroke={color} strokeWidth="1.7" />
      <Path d="M4 21c0-4 4-6 8-6M17 17l4 4M21 17l-4 4" stroke={color} strokeWidth="1.7" strokeLinecap="round" />
    </Svg>
  );
}

export function MenuPasswordIcon({ size = 18, color = GOLD }) {
  return (
    <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <Rect x="5" y="10" width="14" height="11" rx="2.5" stroke={color} strokeWidth="1.7" />
      <Path d="M8 10V7a4 4 0 0 1 8 0v3" stroke={color} strokeWidth="1.7" />
      <Circle cx="12" cy="15" r="1.4" fill={color} />
    </Svg>
  );
}

export function MenuPrivacyIcon({ size = 18, color = GOLD }) {
  return (
    <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <Circle cx="12" cy="8" r="4" stroke={color} strokeWidth="1.7" />
      <Path d="M4 21c0-4 4-6 8-6s8 2 8 6" stroke={color} strokeWidth="1.7" strokeLinecap="round" />
    </Svg>
  );
}

// 78차: 크루 게시판 "탈출기록 공유" 퀵액션 아이콘(스펙 12종엔 없음) — 체크마크,
// 색은 호출부에서 크루 초록 톤(COLORS.green)으로 지정
export function CheckmarkIcon({ size = 14, color = GOLD }) {
  return (
    <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <Path d="M4 12l6 6L20 6" stroke={color} strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
    </Svg>
  );
}

// 78차: 마이페이지 메뉴 12종 스펙엔 없지만(매장관리자/총괄관리자 전용 항목) 같은
// 스타일(골드 라인, 1.7 stroke)로 맞춰 새로 그림 — empty-stores.svg 모티프를 24x24로 축소
export function MenuStoreIcon({ size = 18, color = GOLD }) {
  return (
    <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <Path d="M4 10l1.5-5h13L20 10" stroke={color} strokeWidth="1.7" strokeLinejoin="round" />
      <Path d="M4 10v9h16v-9" stroke={color} strokeWidth="1.7" strokeLinejoin="round" />
      <Path d="M4 10a2.5 2.5 0 0 0 5 0 2.5 2.5 0 0 0 6 0 2.5 2.5 0 0 0 5 0" stroke={color} strokeWidth="1.7" strokeLinejoin="round" />
    </Svg>
  );
}

// 78차: DesignSync로 재조회한 "UNLOCK 에셋.dc.html" "04 UI 아이콘 세트" — 정보/메타,
// 액션, 커뮤니케이션·관리자 3그룹 18종. 전부 24x24, 골드 라인 stroke-width 1.7.
export function SearchIcon({ size = 18, color = GOLD }) {
  return (
    <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <Circle cx="10.5" cy="10.5" r="6.5" stroke={color} strokeWidth="1.7" />
      <Path d="M15.5 15.5L20 20" stroke={color} strokeWidth="1.7" strokeLinecap="round" />
    </Svg>
  );
}

export function PeopleIcon({ size = 18, color = GOLD }) {
  return (
    <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <Circle cx="9" cy="8" r="3.2" stroke={color} strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
      <Path d="M3.5 19.5c0-3.3 2.5-5.5 5.5-5.5s5.5 2.2 5.5 5.5" stroke={color} strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
      <Path d="M15.8 5.4a3 3 0 0 1 0 5.5M17.4 14c2.3.3 4.1 2.4 4.1 5.2" stroke={color} strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
    </Svg>
  );
}

export function LocationIcon({ size = 18, color = GOLD }) {
  return (
    <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <Path d="M12 21s6-5.4 6-10.2A6 6 0 1 0 6 10.8C6 15.6 12 21 12 21z" stroke={color} strokeWidth="1.7" strokeLinejoin="round" />
      <Circle cx="12" cy="10.8" r="2.3" stroke={color} strokeWidth="1.7" />
    </Svg>
  );
}

export function GenreIcon({ size = 18, color = GOLD }) {
  return (
    <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <Path d="M4.5 5c4.5-1.2 10.5-1.2 15 0 0 6.6-2 11.4-7.5 14C6.5 16.4 4.5 11.6 4.5 5z" stroke={color} strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
      <Path d="M8.5 10c.9-.8 2-.8 2.9 0M12.6 10c.9-.8 2-.8 2.9 0" stroke={color} strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
      <Path d="M9 14c1.7 1.4 4.3 1.4 6 0" stroke={color} strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
    </Svg>
  );
}

export function PriceIcon({ size = 18, color = GOLD }) {
  return (
    <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <Path d="M11 3.5H4.5v6.5l9.5 9.5 6.5-6.5L11 3.5z" stroke={color} strokeWidth="1.7" strokeLinejoin="round" />
      <Circle cx="7.8" cy="7.8" r="1.4" stroke={color} strokeWidth="1.7" />
    </Svg>
  );
}

export function PaymentIcon({ size = 18, color = GOLD }) {
  return (
    <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <Rect x="3" y="6" width="18" height="12" rx="2.5" stroke={color} strokeWidth="1.7" />
      <Path d="M3 10h18" stroke={color} strokeWidth="1.7" />
      <Path d="M6.5 14.5h4" stroke={color} strokeWidth="1.7" strokeLinecap="round" />
    </Svg>
  );
}

export function EditIcon({ size = 18, color = GOLD }) {
  return (
    <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <Path d="M14.5 5.5l4 4" stroke={color} strokeWidth="1.7" strokeLinejoin="round" />
      <Path d="M4 20l1-4.2L16.3 4.5a2 2 0 0 1 2.9 2.9L7.9 18.7 4 20z" stroke={color} strokeWidth="1.7" strokeLinejoin="round" />
    </Svg>
  );
}

export function ShareIcon({ size = 18, color = GOLD }) {
  return (
    <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <Path d="M9 5.5H5.5v13h13V15" stroke={color} strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
      <Path d="M14 4.5h6v6M20 4.5l-8.5 8.5" stroke={color} strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
    </Svg>
  );
}

export function RefreshIcon({ size = 18, color = GOLD }) {
  return (
    <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <Path d="M19.5 12a7.5 7.5 0 1 1-2.2-5.3" stroke={color} strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
      <Path d="M19.5 4.5V8h-3.5" stroke={color} strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
    </Svg>
  );
}

export function LinkIcon({ size = 18, color = GOLD }) {
  return (
    <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <Path d="M10 14a4 4 0 0 0 5.7.3l2.5-2.5a4 4 0 1 0-5.6-5.6l-1.4 1.4" stroke={color} strokeWidth="1.7" strokeLinecap="round" />
      <Path d="M14 10a4 4 0 0 0-5.7-.3l-2.5 2.5a4 4 0 1 0 5.6 5.6l1.4-1.4" stroke={color} strokeWidth="1.7" strokeLinecap="round" />
    </Svg>
  );
}

export function CameraIcon({ size = 18, color = GOLD }) {
  return (
    <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <Rect x="3" y="7" width="18" height="13" rx="2.5" stroke={color} strokeWidth="1.7" strokeLinejoin="round" />
      <Path d="M8.3 7l1.5-2.5h4.4L15.7 7" stroke={color} strokeWidth="1.7" strokeLinejoin="round" />
      <Circle cx="12" cy="13.5" r="3.3" stroke={color} strokeWidth="1.7" />
    </Svg>
  );
}

export function SaveIcon({ size = 18, color = GOLD }) {
  return (
    <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <Path d="M5 4.5h11l3.5 3.5V19a.5.5 0 0 1-.5.5H5.5A.5.5 0 0 1 5 19V4.5z" stroke={color} strokeWidth="1.7" strokeLinejoin="round" />
      <Path d="M8 4.5v4.5h7V4.5" stroke={color} strokeWidth="1.7" strokeLinejoin="round" />
      <Rect x="8" y="13" width="8" height="6.5" stroke={color} strokeWidth="1.7" />
    </Svg>
  );
}

export function StatsIcon({ size = 18, color = GOLD }) {
  return (
    <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <Path d="M4 4.5v15h15" stroke={color} strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
      <Path d="M8 16.5v-4M13 16.5V8M18 16.5v-6" stroke={color} strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
    </Svg>
  );
}

export function ReceiptIcon({ size = 18, color = GOLD }) {
  return (
    <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <Path d="M6 3.5h12v17l-2-1.3-2 1.3-2-1.3-2 1.3-2-1.3V3.5z" stroke={color} strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
      <Path d="M9 8h6M9 11.5h6M9 15h4" stroke={color} strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
    </Svg>
  );
}

export function ContractIcon({ size = 18, color = GOLD }) {
  return (
    <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <Path d="M6 3.5h9l3 3v4.5" stroke={color} strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
      <Path d="M6 3.5v17h6" stroke={color} strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
      <Path d="M9 8h5M9 11h3" stroke={color} strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
      <Circle cx="16.5" cy="16.5" r="4" stroke={color} strokeWidth="1.7" />
      <Path d="M16.5 14.7v1.8l1.3 1" stroke={color} strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
    </Svg>
  );
}

export function BuildingIcon({ size = 18, color = GOLD }) {
  return (
    <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <Path d="M3.5 20.5h17" stroke={color} strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
      <Path d="M6 20.5V6l7-2.2v16.7M18 20.5V10l-5-1.9" stroke={color} strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
      <Path d="M9 9h1.5M9 12h1.5M9 15h1.5" stroke={color} strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
    </Svg>
  );
}

export function AdminIcon({ size = 18, color = GOLD }) {
  return (
    <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <Path d="M12 3.2l7 2.5v6c0 4.6-3 7.7-7 9.1-4-1.4-7-4.5-7-9.1v-6l7-2.5z" stroke={color} strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
      <Path d="M9 12l2 2 4-4" stroke={color} strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
    </Svg>
  );
}

export function RewardIcon({ size = 18, color = GOLD }) {
  return (
    <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <Path d="M6.5 3.5h11l3 5-8.5 12L3.5 8.5l3-5z" stroke={color} strokeWidth="1.7" strokeLinejoin="round" />
      <Path d="M3.5 8.5h17M9.5 3.5L6.5 8.5 12 20.5M14.5 3.5l3 5-5.5 12" stroke={color} strokeWidth="1.7" strokeLinejoin="round" />
    </Svg>
  );
}

// 78차 12차: PICK 배너(claude.ai/design "05 PICK BANNER") — translate+scale 트랜스폼을
// 여러 겹 쓴 장식용 SVG였는데, react-native-svg에서 텍스트와 겹쳐 보이는 버그가 두 차례
// 재현돼(78차12차 최초 적용, 78차21차 축소 시도 후에도 재발) 78차23차에 완전히 제거하고
// BookingScreen.js를 SpotlightStarIcon 기반의 평범한 아이콘+텍스트 행으로 교체함.

// 78차 19차: claude.ai/design "UNLOCK 에셋" 프로젝트 "06 매장관리자 아이콘 세트" 섹션을
// 그대로 이식 — 예약관리 탭 상태 아이콘 3종 + 빈 목록 일러스트, 매장설정 탭 섹션
// 아이콘 2종, UNLOCK 기여도 카드 아이콘 5종.
export function HourglassIcon({ size = 18, color = GOLD }) {
  return (
    <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <Path d="M6 3.5h12M6 20.5h12" stroke={color} strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
      <Path d="M7 3.5v2.3c0 2.4 1.9 4 5 5.2 3.1-1.2 5-2.8 5-5.2V3.5M7 20.5v-2.3c0-2.4 1.9-4 5-5.2 3.1 1.2 5 2.8 5 5.2v2.3" stroke={color} strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
    </Svg>
  );
}

export function CancelIcon({ size = 18, color = GOLD }) {
  return (
    <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <Circle cx="12" cy="12" r="8.5" stroke={color} strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
      <Path d="M9.3 9.3l5.4 5.4M14.7 9.3l-5.4 5.4" stroke={color} strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
    </Svg>
  );
}

export function CalendarCheckIcon({ size = 18, color = GOLD }) {
  return (
    <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <Rect x="3.5" y="5" width="17" height="15.5" rx="2.3" stroke={color} strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
      <Path d="M3.5 9.5h17" stroke={color} strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
      <Path d="M8 3v3M16 3v3" stroke={color} strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
      <Path d="M8.5 14.3l2 2 4.5-4.6" stroke={color} strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
    </Svg>
  );
}

export function EmptyReservationsIcon({ size = 64 }) {
  return (
    <EmptyFrame size={size}>
      <Rect x="35" y="34" width="26" height="30" rx="3" stroke={GOLD} strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
      <Path d="M35 41h26" stroke={GOLD} strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
      <Path d="M41 30v5M55 30v5" stroke={GOLD} strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
      <Path d="M41.5 51h13M41.5 57h9" stroke={GOLD} strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
    </EmptyFrame>
  );
}

export function ClockIcon({ size = 18, color = GOLD }) {
  return (
    <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <Circle cx="12" cy="12" r="8.5" stroke={color} strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
      <Path d="M12 7.5V12l3.3 2" stroke={color} strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
    </Svg>
  );
}

export function SpotlightStarIcon({ size = 18, color = GOLD }) {
  // 78차 35차: 원본 path가 y=1~16.2 범위에만 그려져 있어 24x24 viewBox 안에서 위로
  // 쏠려 보임(옆 텍스트와 같은 줄에 두면 텍스트보다 위에 떠 보이는 원인) — translate로
  // 시각적 중심을 viewBox 중앙(y=12)에 맞춤.
  return (
    <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <G transform="translate(0, 3.4)">
        <Path d="M12 3l2 4.4 4.8.6-3.5 3.4.9 4.8L12 14l-4.2 2.2.9-4.8-3.5-3.4 4.8-.6L12 3z" stroke={color} strokeWidth="1.7" strokeLinejoin="round" />
        <Path d="M12 3V1M4.5 8l-1.5-1M19.5 8l1.5-1" stroke={color} strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
      </G>
    </Svg>
  );
}

export function ContribFavoriteIcon({ size = 18, color = GOLD }) {
  return (
    <Svg width={size} height={size} viewBox="0 0 18 18" fill="none">
      <Path d="M9 15.5S2.5 11.6 2.5 6.9A3.4 3.4 0 0 1 9 5.4a3.4 3.4 0 0 1 6.5 1.5c0 4.7-6.5 8.6-6.5 8.6z" stroke={color} strokeWidth="1.7" strokeLinejoin="round" />
    </Svg>
  );
}

export function ContribReviewIcon({ size = 18, color = GOLD }) {
  return (
    <Svg width={size} height={size} viewBox="0 0 18 18" fill="none">
      <Path d="M2.5 3.5h13v9h-6.2L6 15.5v-3H2.5v-9z" stroke={color} strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
      <Path d="M9 6l1 2 2.2.3-1.6 1.5.4 2.2L9 10.9l-2 1.1.4-2.2-1.6-1.5L8 6z" stroke={color} strokeWidth="1.3" strokeLinejoin="round" />
    </Svg>
  );
}

export function ContribRatingIcon({ size = 18, color = GOLD }) {
  return (
    <Svg width={size} height={size} viewBox="0 0 18 18" fill={color} stroke={color} strokeWidth="1.7">
      <Path d="M9 2.2l2.1 4.3 4.7.7-3.4 3.3.8 4.7L9 13l-4.2 2.2.8-4.7-3.4-3.3 4.7-.7L9 2.2z" strokeLinejoin="round" />
    </Svg>
  );
}

export function ViewsIcon({ size = 18, color = GOLD }) {
  return (
    <Svg width={size} height={size} viewBox="0 0 18 18" fill="none">
      <Path d="M1.5 9c1.8-3.3 4.4-5 7.5-5s5.7 1.7 7.5 5c-1.8 3.3-4.4 5-7.5 5S3.3 12.3 1.5 9z" stroke={color} strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
      <Circle cx="9" cy="9" r="2.3" stroke={color} strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
    </Svg>
  );
}

export function TrendingUpIcon({ size = 18, color = GOLD }) {
  return (
    <Svg width={size} height={size} viewBox="0 0 18 18" fill="none">
      <Path d="M2 13.5l4.5-5 3 3 6-6.5" stroke={color} strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
      <Path d="M11.8 5h3.7v3.7" stroke={color} strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
    </Svg>
  );
}

// 78차 37차: DesignSync "UNLOCK 에셋.dc.html" "07 추가 요청 아이콘 세트" — 실기기 테스트에서
// 지목된 마지막 이모지 잔재 11종. 24x24, 골드 라인 stroke-width 1.7(색은 알림 타입별로 다름).
export function RecruitDifficultyGaugeIcon({ size = 18, color = GOLD }) {
  return (
    <Svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.7" strokeLinecap="round">
      <Path d="M4 18V13M9.5 18V9M15 18V6M20 18v-9" />
    </Svg>
  );
}

export function StopwatchIcon({ size = 18, color = GOLD }) {
  return (
    <Svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
      <Path d="M9 2.5h6M12 2.5v2.7" />
      <Circle cx="12" cy="13.5" r="8" />
      <Path d="M12 9v4.5l3 2" />
    </Svg>
  );
}

export function DifficultyTargetIcon({ size = 18, color = GOLD }) {
  return (
    <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <Circle cx="12" cy="12" r="8.5" stroke={color} strokeWidth="1.7" />
      <Circle cx="12" cy="12" r="5" stroke={color} strokeWidth="1.7" />
      <Circle cx="12" cy="12" r="1.5" fill={color} stroke="none" />
    </Svg>
  );
}

// 78차 37차: "난이도 4단계" — 자물쇠 모양은 4단계 전부 동일(사용자 지시: 단계별로 자물쇠를
// 쌓거나 바꾸면 형태가 무너짐), 난이도는 이 아이콘과 별개인 DifficultyGaugeDots(도트)로만 표현.
export function DifficultyLockIcon({ size = 24, color = GOLD }) {
  return (
    <Svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
      <Rect x="5" y="10.5" width="10" height="8" rx="1.8" />
      <Path d="M7.3 10.5V7.3a3.7 3.7 0 0 1 7.4 0v3.2" />
    </Svg>
  );
}

const DIFFICULTY_LEVEL = { easy: 1, normal: 2, hard: 3, expert: 4 };
export function DifficultyGaugeDots({ difficulty, size = 6, gap = 4, color = GOLD, mutedColor = '#4a4a56' }) {
  const filled = DIFFICULTY_LEVEL[difficulty] ?? 0;
  return (
    <View style={{ flexDirection: 'row', gap }}>
      {[0, 1, 2, 3].map(i => (
        <View key={i} style={{
          width: size, height: size, borderRadius: size / 2,
          backgroundColor: i < filled ? color : 'transparent',
          borderWidth: i < filled ? 0 : 1.4, borderColor: mutedColor,
        }} />
      ))}
    </View>
  );
}

// 78차 37차: "예약하기" 버튼 프리픽스 — GradientButton에 Icon prop 신규 추가하며 반영.
export function BookingLockIcon({ size = 18, color = '#F5F0E8' }) {
  return (
    <Svg width={size} height={size} viewBox="0 0 18 18" fill="none" stroke={color} strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
      <Rect x="4" y="8" width="10" height="7" rx="1.6" />
      <Path d="M6.3 8V5.6a2.7 2.7 0 0 1 5.4 0V8" />
    </Svg>
  );
}

// 78차 39차: DesignSync "UNLOCK 에셋.dc.html" "08 크루 게시판 아이콘" — 사용자가 직접
// 구성한 "테마 추천"(별+체크) 아이콘. 지금까지 이 자리엔 전용 아이콘이 없어 MenuReviewIcon
// (리뷰 메뉴용 별 아이콘)을 임시로 재사용하고 있었음.
export function ThemeRecommendIcon({ size = 18, color = GOLD }) {
  return (
    <Svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.7" strokeLinejoin="round" strokeLinecap="round">
      <Path d="M12 3l2.4 4.9 5.4.8-3.9 3.8.9 5.4L12 15.3l-4.8 2.6.9-5.4-3.9-3.8 5.4-.8L12 3z" />
      <Path d="M9.3 12.3l2 2 3.6-4" strokeWidth="1.5" />
    </Svg>
  );
}

export function ReservationCancelIcon({ size = 18, color = COLORS.red }) {
  return (
    <Svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
      <Rect x="3.5" y="5" width="17" height="15.5" rx="2.3" />
      <Path d="M3.5 9.5h17" />
      <Path d="M8 3v3M16 3v3" />
      <Path d="M9.3 13.3l4.4 4.4M13.7 13.3l-4.4 4.4" />
    </Svg>
  );
}

export function ParticipantAcceptIcon({ size = 18, color = COLORS.green }) {
  return (
    <Svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
      <Circle cx="10" cy="9" r="3.1" />
      <Path d="M4.8 19.5c0-3.1 2.3-5.1 5.2-5.1s5.2 2 5.2 5.1" />
      <Path d="M15.5 4.5l1 2M19.5 8l2 .6M17.5 12.5l1.6 1.6" />
      <Path d="M9 8.3l.9 1.6 1.7.2-1.3 1.2.3 1.7L9 12.1l-1.6.9.3-1.7-1.3-1.2 1.7-.2z" strokeWidth="1.3" />
    </Svg>
  );
}

export function EmptySearchResultIcon({ size = 64 }) {
  return (
    <EmptyFrame size={size}>
      <Circle cx="45" cy="45" r="9.5" stroke={GOLD} strokeWidth="1.7" />
      <Path d="M52 52L59 59" stroke={GOLD} strokeWidth="1.7" strokeLinecap="round" />
    </EmptyFrame>
  );
}

export function ReviewWriteableIcon({ size = 64 }) {
  return (
    <EmptyFrame size={size}>
      <Path d="M36 49l8 8 16-17" stroke={GOLD} strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" />
    </EmptyFrame>
  );
}

export function ReviewNotWriteableIcon({ size = 64 }) {
  return (
    <EmptyFrame size={size} color={COLORS.red}>
      <Path d="M48 36v14" stroke={COLORS.red} strokeWidth="1.9" strokeLinecap="round" />
      <Circle cx="48" cy="58" r="1.6" fill={COLORS.red} stroke="none" />
    </EmptyFrame>
  );
}

// 78차 55차: claude.ai/design "UNLOCK 에셋" 프로젝트 "09 매장관리자 홈·통계 아이콘 세트"
// — StoreAdminScreen.js의 홈/통계 탭에 남아있던 이모지(📊☀️📦❌📋🏆) 대체용 7종.
export function DashboardHomeIcon({ size = 18, color = GOLD }) {
  return (
    <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <Rect x="3.5" y="3.5" width="8" height="8" rx="1.6" stroke={color} strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
      <Rect x="12.5" y="3.5" width="8" height="5" rx="1.6" stroke={color} strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
      <Rect x="12.5" y="10.5" width="8" height="10" rx="1.6" stroke={color} strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
      <Rect x="3.5" y="13.5" width="8" height="7" rx="1.6" stroke={color} strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
    </Svg>
  );
}

export function TodayRevenueIcon({ size = 18, color = GOLD }) {
  return (
    <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <Circle cx="9" cy="9" r="6" stroke={color} strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
      <Path d="M9 5.5V9l2.4 1.4" stroke={color} strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
      <Path d="M14.5 15.5h6M17.5 12.5v6" stroke={color} strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
    </Svg>
  );
}

export function MonthlyBookingsIcon({ size = 18, color = GOLD }) {
  return (
    <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <Rect x="3.5" y="5" width="17" height="15.5" rx="2.3" stroke={color} strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
      <Path d="M3.5 9.5h17" stroke={color} strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
      <Path d="M8 3v3M16 3v3" stroke={color} strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
      <Path d="M8.5 14.3l2 2 4.5-4.6" stroke={color} strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
    </Svg>
  );
}

export function MonthlyCancelsIcon({ size = 18, color = GOLD }) {
  return (
    <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <Rect x="3.5" y="5" width="17" height="15.5" rx="2.3" stroke={color} strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
      <Path d="M3.5 9.5h17" stroke={color} strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
      <Path d="M8 3v3M16 3v3" stroke={color} strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
      <Path d="M9.3 13.3l4.4 4.4M13.7 13.3l-4.4 4.4" stroke={color} strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
    </Svg>
  );
}

export function OccupancyGaugeIcon({ size = 18, color = GOLD }) {
  return (
    <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <Path d="M4 15.5a8 8 0 0 1 16 0" stroke={color} strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
      <Path d="M12 15.5V8.5M12 15.5l4.2-4.6" stroke={color} strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
    </Svg>
  );
}

export function TodayBookingClipboardIcon({ size = 18, color = GOLD }) {
  return (
    <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <Rect x="5" y="4" width="14" height="17" rx="2" stroke={color} strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
      <Path d="M9 4V3a1.2 1.2 0 0 1 1.2-1.2h3.6A1.2 1.2 0 0 1 15 3v1" stroke={color} strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
      <Circle cx="14.3" cy="14.3" r="4" stroke={color} strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
      <Path d="M14.3 12.3v2l1.3 1" stroke={color} strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
    </Svg>
  );
}

export function EscapeSuccessRateIcon({ size = 18, color = GOLD }) {
  return (
    <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <Path d="M12 3.2l7 2.5v6c0 4.6-3 7.7-7 9.1-4-1.4-7-4.5-7-9.1v-6l7-2.5z" stroke={color} strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
      <Path d="M9 12l2 2 4-4" stroke={color} strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
    </Svg>
  );
}

// 78차 71차: claude.ai/design "UNLOCK 에셋" 프로젝트 "11. 리뷰 작성 아이콘 세트" 섹션에서
// 실제 SVG를 그대로 옮김 — WriteReviewScreen.js의 기본 이모지(★ Unicode 별/🔐 난이도/
// 🎭 연출·인테리어/⚠️ 스포일러)를 대체.
// 별점(선택/서브평가 공용) — 선택 안 된 별은 무채색 아웃라인, 선택된 별만 골드로 꽉 채움
// (디자인 목업은 전부 골드 스트로크+fill-opacity 단계였지만, 탭으로 고르는 입력 UI라
// 뭐가 선택됐는지 한눈에 구분되도록 기존 앱의 선택/비선택 색상 관례를 그대로 유지).
export function StarIcon({ size = 24, filled = false, color = GOLD, emptyColor = '#3a3a48' }) {
  return (
    <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <Path
        d="M12 3l2.4 4.9 5.4.8-3.9 3.8.9 5.4L12 15.3l-4.8 2.6.9-5.4-3.9-3.8 5.4-.8L12 3z"
        fill={filled ? color : 'none'}
        stroke={filled ? color : emptyColor}
        strokeWidth="1.7"
        strokeLinejoin="round"
      />
    </Svg>
  );
}

export function DifficultyRatingIcon({ size = 24, color = GOLD }) {
  return (
    <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <Rect x="5" y="10.5" width="10" height="8" rx="1.8" stroke={color} strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
      <Path d="M7.3 10.5V7.3a3.7 3.7 0 0 1 7.4 0v3.2" stroke={color} strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
    </Svg>
  );
}

export function ProductionRatingIcon({ size = 24, color = GOLD }) {
  return (
    <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <Path d="M12 3v3.3M12 3l2 2M12 3l-2 2" stroke={color} strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
      <Circle cx="12" cy="12" r="5" stroke={color} strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
      <Path d="M4 20.5c1.6-2.4 4.6-3.8 8-3.8s6.4 1.4 8 3.8" stroke={color} strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
    </Svg>
  );
}

export function SpoilerWarningIcon({ size = 24, color = GOLD }) {
  return (
    <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <Path
        d="M4 5l16 14M12 3.5l9 4.5-2.6 1.3M4.6 9.3L3 8l9-4.5M6 10.3v4.4c0 1.7 2.7 3 6 3 1.4 0 2.7-.2 3.7-.6M18 12.6v1.1"
        stroke={color} strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"
      />
    </Svg>
  );
}

// 78차 72차: claude.ai/design "UNLOCK 디자인" 프로젝트의 크루 탭 목업(크루 게시판 카드 4종
// + 크루 목록 엠블럼)에서 실제 SVG를 그대로 옮김 — CrewScreen.js의 게시글 타입 이모지
// (💬/📋/🔐 자물쇠 엠블럼)를 대체. 테마추천은 이미 별도 ThemeRecommendIcon(78차 39차)이
// 있고, 탈출기록은 기존 CheckmarkIcon과 모양이 사실상 같아 재사용(중복 컴포넌트 생성 안 함).
export function CrewMessageIcon({ size = 16, color = '#6E86C4' }) {
  return (
    <Svg width={size} height={size} viewBox="0 0 20 20" fill="none">
      <Path d="M3 5h14v9H8l-4 3v-3H3z" stroke={color} strokeWidth="1.6" strokeLinejoin="round" />
    </Svg>
  );
}

export function CrewRecruitLinkIcon({ size = 16, color = '#D98A6E' }) {
  return (
    <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <Circle cx="9" cy="7" r="3" stroke={color} strokeWidth="1.7" />
      <Path d="M3 20c0-3 3-4.5 6-4.5M16 8h5M18.5 5.5v5" stroke={color} strokeWidth="1.7" strokeLinecap="round" />
    </Svg>
  );
}

export function CrewNoticeIcon({ size = 18, color = GOLD }) {
  return (
    <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <Path d="M4 5h13l3 3v11H4z" stroke={color} strokeWidth="1.7" strokeLinejoin="round" />
      <Path d="M8 10h8M8 14h5" stroke={color} strokeWidth="1.7" strokeLinecap="round" />
    </Svg>
  );
}

// 크루 대표 이미지가 없을 때 쓰는 기본 엠블럼(자물쇠+열쇠) — 기존 "🔐" 이모지 대체
export function CrewEmblemIcon({ size = 30, color = '#241a06' }) {
  return (
    <Svg width={size} height={size} viewBox="0 0 100 100" fill="none">
      <Rect x="16" y="16" width="68" height="68" rx="18" stroke={color} strokeWidth="3.6" />
      <Rect x="33" y="33" width="34" height="34" rx="10" stroke={color} strokeWidth="3.2" strokeOpacity="0.65" />
      <Circle cx="50" cy="46" r="6" fill={color} />
      <Path d="M47 51 L45 62 H55 L53 51 Z" fill={color} />
    </Svg>
  );
}

// 78차 73차: claude.ai/design "UNLOCK 에셋" 프로젝트 "10. 내 탈출 기록 아이콘 세트"에서
// 실제 SVG를 그대로 옮김 — EscapeDashboardScreen.js의 이모지(✅❌⏱️🏆💡🎭🔒🔓)를 대체.
// 기존 EscapeSuccessIcon/EscapeFailIcon(96x96 이중 링, 빈 화면 일러스트용)과는 톤이 달라
// 이름을 구분(이 세트는 24px 단일 원, 통계 카드용).
export function EscapeRecordSuccessIcon({ size = 24, color = GOLD }) {
  return (
    <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <Circle cx="12" cy="12" r="8.5" stroke={color} strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
      <Path d="M8.5 12.3l2.4 2.4 4.6-5" stroke={color} strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
    </Svg>
  );
}

export function EscapeRecordFailIcon({ size = 24, color = GOLD }) {
  return (
    <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <Circle cx="12" cy="12" r="8.5" stroke={color} strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
      <Path d="M9.3 9.3l5.4 5.4M14.7 9.3l-5.4 5.4" stroke={color} strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
    </Svg>
  );
}

export function AvgEscapeTimeIcon({ size = 24, color = GOLD }) {
  return (
    <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <Path d="M9 2.5h6M12 2.5v2.7" stroke={color} strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
      <Circle cx="12" cy="13.5" r="8" stroke={color} strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
      <Path d="M12 9v4.5l3 2" stroke={color} strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
    </Svg>
  );
}

export function BestRecordIcon({ size = 24, color = GOLD }) {
  return (
    <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <Path d="M8 3.5h8v3a4 4 0 0 1-8 0v-3z" stroke={color} strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
      <Path d="M8 4.5H5.5a2.5 2.5 0 0 0 2.5 3.7M16 4.5h2.5a2.5 2.5 0 0 1-2.5 3.7" stroke={color} strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
      <Path d="M12 10.5v3.2" stroke={color} strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
      <Path d="M9 20.5h6M9.5 17.2h5l.3 3.3h-5.6z" stroke={color} strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
    </Svg>
  );
}

export function HintUsageIcon({ size = 24, color = GOLD }) {
  return (
    <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <Path d="M9 16.5h6M9.5 19h5" stroke={color} strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
      <Path d="M8 10.5a4 4 0 1 1 8 0c0 1.9-1.1 2.7-1.7 3.6-.4.6-.6 1.1-.6 1.9H10.3c0-.8-.2-1.3-.6-1.9-.6-.9-1.7-1.7-1.7-3.6z" stroke={color} strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
      <Path d="M12 3v1.4M5 6.5l1 1M19 6.5l-1 1" stroke={color} strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
    </Svg>
  );
}

export function GenrePreferenceIcon({ size = 24, color = GOLD }) {
  return (
    <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <Path d="M3.5 7c3-1 6.5-1 8.5.7 2-1.7 5.5-1.7 8.5-.7 0 5.4-1.6 9.3-6.1 11.5-.9-.4-1.7-.9-2.4-1.5-.7.6-1.5 1.1-2.4 1.5C5.1 16.3 3.5 12.4 3.5 7z" stroke={color} strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
      <Path d="M7 10.2c.7-.6 1.5-.6 2.2 0M14.8 10.2c.7-.6 1.5-.6 2.2 0" stroke={color} strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
      <Path d="M8.1 13.5c1.2 1 2.6 1 3.9 0M12 13.5c1.2 1 2.6 1 3.9 0" stroke={color} strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
    </Svg>
  );
}

export function StatsVisibilityIcon({ size = 24, color = GOLD }) {
  return (
    <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <Rect x="3.5" y="11" width="9" height="8" rx="1.8" stroke={color} strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
      <Path d="M5.8 11V7.8a3.7 3.7 0 0 1 6.6-2.3" stroke={color} strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
      <Rect x="14.5" y="11" width="6" height="8" rx="1.6" opacity="0.5" stroke={color} strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
      <Path d="M15.7 11V8.5a2.4 2.4 0 0 1 4.8 0V11" opacity="0.5" stroke={color} strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
    </Svg>
  );
}

// 79차: "UNLOCK 에셋" 디자인 프로젝트 섹션 14(알림 아이콘 세트) 이식 — NotificationsScreen.js의
// TYPE_CONFIG 중 Icon 필드가 없어 기본 이모지로만 표시되던 6개 타입 전용.
export function RecruitRejectedIcon({ size = 18, color = GOLD }) {
  return (
    <Svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
      <Circle cx="10.5" cy="8" r="3.2" />
      <Path d="M5 19c0-3.3 2.5-5.5 5.5-5.5 1 0 1.9.2 2.7.7" />
      <Path d="M15.5 14.5l3.5 3.5M19 14.5l-3.5 3.5" />
    </Svg>
  );
}

export function RecruitAppliedIcon({ size = 18, color = GOLD }) {
  return (
    <Svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
      <Path d="M3.5 6.5h17v11h-17z" />
      <Path d="M3.5 6.5l8.5 6.5 8.5-6.5" />
      <Circle cx="19.5" cy="5.5" r="2.6" fill={color} stroke="none" />
    </Svg>
  );
}

export function NoShowPenaltyIcon({ size = 18, color = COLORS.red }) {
  return (
    <Svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
      <Path d="M12 3.5l9 16h-18z" />
      <Path d="M12 10v4M12 16.7v.1" />
    </Svg>
  );
}

export function OverdueIcon({ size = 18, color = COLORS.red }) {
  return (
    <Svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
      <Circle cx="11" cy="12.5" r="7.5" />
      <Path d="M11 7.5V12.5l3.6 2" />
      <Path d="M17.3 5.3l1.4-1.4M18.7 3.9l1.4 1.4" />
    </Svg>
  );
}

export function ContractExpiringIcon({ size = 18, color = GOLD }) {
  return (
    <Svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
      <Path d="M6 3.5h9l3 3v13.5a.5.5 0 0 1-.5.5H6.5a.5.5 0 0 1-.5-.5V4a.5.5 0 0 1 .5-.5z" />
      <Path d="M15 3.5v3h3" />
      <Path d="M9 11h6M9 14.5h6M9 18h4" />
    </Svg>
  );
}

export function ManualEntryReminderIcon({ size = 18, color = GOLD }) {
  return (
    <Svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
      <Rect x="4" y="4.5" width="12" height="15" rx="1.8" />
      <Path d="M14.5 5.5l4 4" />
      <Path d="M9 20.5l1-4.2L20.3 4.5a2 2 0 0 1 2.9 2.9L13.9 18.7 9 20.5z" />
    </Svg>
  );
}

// 79차: "UNLOCK 에셋" 섹션 13(시즌 이벤트 배지) — HomeScreen.js의 시즌 이벤트 배너 카드가
// 썸네일 없을 때 44px 기본 이모지(🎃)로 채우던 자리 전용.
export function HalloweenBadgeIcon({ size = 44, color = GOLD }) {
  return (
    <Svg width={size} height={size} viewBox="0 0 44 44" fill="none" stroke={color} strokeWidth="1.6" strokeLinejoin="round">
      <Path d="M22 12c-8 0-12.5 5-12.5 11.5S14.5 35 22 35s12.5-5.5 12.5-11.5S30 12 22 12z" />
      <Path d="M15 13.5c0 5.5 1 21 0 21M22 12c0 6 .8 23 0 23M29 13.5c0 5.5-1 21 0 21" strokeWidth="1.2" opacity="0.55" strokeLinecap="round" />
      <Path d="M22 12V8.3" strokeLinecap="round" />
      <Path d="M22 8.3c-1.3-.3-2-1.2-1.8-2.7 1.7-.4 3 .5 3.3 1.8" strokeLinecap="round" />
      <Path d="M16.5 21.5l2.3 2.3M18.8 21.5l-2.3 2.3" strokeLinecap="round" />
      <Path d="M25.2 21.5l2.3 2.3M27.5 21.5l-2.3 2.3" strokeLinecap="round" />
      <Path d="M16.5 29c2 1.6 9 1.6 11 0" strokeLinecap="round" />
    </Svg>
  );
}

// 79차: "UNLOCK 에셋" 섹션 17(신규 아이콘 세트 9종) — 이모지 전수조사(fork)에서 대응
// 아이콘이 없어 "신규 필요"로 분류됐던 9종. claude.ai/design에서 그대로 받아 이식.
export function PrivacyLockIcon({ size = 18, color = GOLD }) {
  return (
    <Svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
      <Path d="M6.5 11h11v9h-11z" />
      <Path d="M8.5 11V7.5a3.5 3.5 0 0 1 7 0V11" />
    </Svg>
  );
}

export function WarningIcon({ size = 18, color = GOLD }) {
  return (
    <Svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
      <Path d="M12 2.8l9.3 16.4a1 1 0 0 1-.9 1.5H3.6a1 1 0 0 1-.9-1.5L12 2.8z" />
      <Path d="M12 9v4.5" />
      <Circle cx="12" cy="17" r="1" fill={color} stroke="none" />
    </Svg>
  );
}

export function CrownIcon({ size = 18, color = GOLD }) {
  return (
    <Svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
      <Path d="M4 9l3.2 3 4.8-6 4.8 6L20 9l-1.6 8H5.6L4 9z" />
      <Path d="M5.6 20h12.8" />
    </Svg>
  );
}

export function PhoneIcon({ size = 18, color = GOLD }) {
  return (
    <Svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
      <Path d="M6 3.5c1.2 0 2.3.9 2.7 2.1l.6 1.9c.2.7 0 1.5-.5 2l-1.2 1.1c1 2.2 2.8 4 5 5l1.1-1.2c.5-.5 1.3-.7 2-.5l1.9.6c1.2.4 2.1 1.5 2.1 2.7v1.6c0 1.4-1.2 2.5-2.6 2.3C9.9 20.2 3.8 14.1 3 6.9 2.9 5.5 4 4.3 5.4 4.3H6z" />
    </Svg>
  );
}

export function EmailIcon({ size = 18, color = GOLD }) {
  return (
    <Svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
      <Path d="M3.5 6.5h17v11h-17z" />
      <Path d="M3.5 6.5l8.5 6.5 8.5-6.5" />
    </Svg>
  );
}

export function TrashIcon({ size = 18, color = GOLD }) {
  return (
    <Svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
      <Path d="M4.5 6h15M7.5 6l1-2.5h7l1 2.5" />
      <Path d="M6 6l1 13.5a1.5 1.5 0 0 0 1.5 1.4h7a1.5 1.5 0 0 0 1.5-1.4L18 6" />
      <Path d="M9.7 9.5v7.5M14.3 9.5v7.5" />
    </Svg>
  );
}

export function ReportFlagIcon({ size = 18, color = GOLD }) {
  return (
    <Svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
      <Path d="M6 3v18" />
      <Path d="M6 4l12 2.3-3.2 3.2L18 12.7 6 15" />
    </Svg>
  );
}

export function NoEntryIcon({ size = 18, color = GOLD }) {
  return (
    <Svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.7">
      <Circle cx="12" cy="12" r="8.5" />
      <Path d="M6.5 17.5l11-11" strokeLinecap="round" />
    </Svg>
  );
}

export function PersonIcon({ size = 18, color = GOLD }) {
  return (
    <Svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.7" strokeLinecap="round">
      <Circle cx="12" cy="7.5" r="3.3" />
      <Path d="M5.5 19.5c0-3.6 2.9-5.9 6.5-5.9s6.5 2.3 6.5 5.9" />
    </Svg>
  );
}

export function HelpCircleIcon({ size = 18, color = GOLD }) {
  return (
    <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <Circle cx="12" cy="12" r="8.5" stroke={color} strokeWidth="1.7" />
      <Path d="M9.6 9.4a2.4 2.4 0 0 1 4.6.9c0 1.6-2.2 1.9-2.2 3.4" stroke={color} strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
      <Circle cx="12" cy="16.6" r="0.9" fill={color} />
    </Svg>
  );
}

// 80차 계속: 크루 랭킹 1/2/3위 메달 — claude.ai/design "UNLOCK 에셋" 18번 섹션에서 이식.
// 순위별 고정 색상(금/은/동)이라 다른 아이콘과 달리 color prop을 굳이 안 받음.
export function RankMedalGoldIcon({ size = 18 }) {
  return (
    <Svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="#D8AE52" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
      <Path d="M8.6 2.5L11 8M15.4 2.5L13 8" />
      <Circle cx="12" cy="14.8" r="6.7" />
      <Path d="M10.8 12.6L12.3 11.7V18" />
    </Svg>
  );
}
export function RankMedalSilverIcon({ size = 18 }) {
  return (
    <Svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="#B8B8C4" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
      <Path d="M8.6 2.5L11 8M15.4 2.5L13 8" />
      <Circle cx="12" cy="14.8" r="6.7" />
      <Path d="M10.1 12.5a1.9 1.9 0 0 1 3.8.5c0 1.6-3.8 3-3.8 5h4" />
    </Svg>
  );
}
export function TrophyIcon({ size = 18, color = GOLD }) {
  return (
    <Svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
      <Path d="M7.5 3.5h9v5a4.5 4.5 0 0 1-9 0v-5z" />
      <Path d="M7.5 5H4.8a3 3 0 0 0 2.9 4M16.5 5h2.7a3 3 0 0 1-2.9 4" />
      <Path d="M12 13v3.5" />
      <Path d="M8.5 20.5h7M9.5 16.5h5l.4 4h-5.8z" />
    </Svg>
  );
}
export function RankMedalBronzeIcon({ size = 18 }) {
  return (
    <Svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="#C17A4D" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
      <Path d="M8.6 2.5L11 8M15.4 2.5L13 8" />
      <Circle cx="12" cy="14.8" r="6.7" />
      <Path d="M10.2 12.3h3.6l-2 2.3a2 2 0 1 1-1.7 3" />
    </Svg>
  );
}
// 80차 계속: claude.ai/design "UNLOCK 에셋" 20번 섹션(매너 평가·매칭 완료)에서 이식 —
// 매너평가는 A안(thumbs-up)을 채택, B안(heart-check)은 디자인에만 남겨둠.
export function MannerLikeIcon({ size = 18, color = GOLD }) {
  return (
    <Svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
      <Path d="M7 10.5l3.4-6.3c.3-.6 1-.9 1.6-.7.9.3 1.4 1.2 1.2 2.1L12.6 9h4.9a2 2 0 0 1 1.9 2.5l-1.6 6.6a2 2 0 0 1-1.9 1.5H7z" />
      <Path d="M3.5 10.5h3.5v9.1H3.5z" />
    </Svg>
  );
}
export function MatchCompleteIcon({ size = 18, color = GOLD }) {
  return (
    <Svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
      <Path d="M3.2 20.8l4.4-11 8.4 8.4-12.8 2.6z" />
      <Path d="M8.5 13.3l3.2 3.2" />
      <Path d="M13.5 8.5c.6-1.6 2.3-2.4 3.9-1.8M15.8 4.3c1.1-1 2.8-1 3.9.1M18.9 11.2c1.6-.5 3.3.3 3.9 1.9" />
      <Path d="M20.6 7.4l.1.1" />
    </Svg>
  );
}
